import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Collapse,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

const SearchableSidebar = ({
  sections,
  onSectionClick,
  onItemClick,
  activeSectionId,
  activeItemId,
  isOpen,
  onToggle,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSectionId, setExpandedSectionId] = useState(null);

  useEffect(() => {
    if (activeSectionId) {
      setExpandedSectionId(activeSectionId);
    }
  }, [activeSectionId]);

  const filteredSections = sections
    .map((section) => ({
      ...section,
      type: "",
      items: section.items.filter((item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    }))
    .filter((section) => section.items.length > 0);

  const toggleSection = (sectionId) => {
    setExpandedSectionId((prev) => (prev === sectionId ? null : sectionId));
  };

  const handleSectionClick = (section) => {
    onSectionClick(section);

    setExpandedSectionId(section.id);
  };
  const typeColors = {
    powerbi: "purple",
    tableau: "orange",
    directory: "blue",
    static: "gray",
  };
  return (
    <Box
      bg="white"
      borderLeft="1px"
      borderColor="gray.200"
      transition="all 0.3s"
      w={isOpen ? "320px" : "50px"}
      overflowY="hidden"
      display="flex"
      flexDirection="column"
    >
      {/* Toggle Button */}
      <Box
        position="sticky"
        top={0}
        bg="white"
        zIndex={2}
        borderBottom="1px"
        borderColor="gray.200"
        p={2}
        flexShrink={0}
      >
        <IconButton
          icon={isOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          onClick={onToggle}
          size="sm"
          aria-label="Toggle sidebar"
        />
      </Box>

      {/* Sidebar Content */}
      <Collapse
        in={isOpen}
        animateOpacity
        style={{ flex: 1, overflow: "hidden" }}
      >
        <Box display="flex" flexDirection="column" height="100%">
          {/* Search Input */}
          <Box p={4} borderBottom="1px" borderColor="gray.100" flexShrink={0}>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                borderRadius="md"
              />
            </InputGroup>
          </Box>

          {/* Scrollable Content */}
          <Box flex={1} overflowY="auto" px={4} py={4}>
            <VStack align="stretch" spacing={2}>
              {filteredSections.length > 0 ? (
                filteredSections.map((section) => (
                  <Box key={section.id}>
                    {/* Section Header - Collapsible */}
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      py={3}
                      px={3}
                      cursor="pointer"
                      borderRadius="md"
                      bg={
                        activeSectionId === section.title
                          ? "teal.50"
                          : "gray.50"
                      }
                      borderLeft={
                        activeSectionId === section.title ? "3px solid" : "none"
                      }
                      borderColor="teal.500"
                      transition="all 0.2s"
                      _hover={{
                        bg:
                          activeSectionId === section.title
                            ? "teal.100"
                            : "gray.100",
                      }}
                      onClick={() => handleSectionClick(section)}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color={
                          activeSectionId === section.title
                            ? "teal.700"
                            : "gray.700"
                        }
                        flex={1}
                      >
                        {section.title}
                      </Text>
                      <IconButton
                        icon={
                          expandedSectionId === section.title ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        }
                        size="xs"
                        // variant="ghost"
                        border="none"
                        bg="none"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSection(section.title);
                        }}
                        aria-label="Toggle section"
                      />
                    </Box>

                    {/* Section Items - Collapsible */}
                    <Collapse
                      in={expandedSectionId === section.title}
                      animateOpacity
                    >
                      <VStack align="stretch" spacing={1} mt={2} ml={2}>
                        {section.items.map((item) => {
                          const badgeColor = typeColors[item.type] || "gray";

                          return (
                            <Link
                              key={item.id}
                              onClick={() => onItemClick(item)}
                              fontSize="sm"
                              color={
                                activeItemId === item.id
                                  ? "blue.600"
                                  : "gray.600"
                              }
                              fontWeight={
                                activeItemId === item.id ? "semibold" : "normal"
                              }
                              py={2}
                              px={3}
                              borderRadius="md"
                              bg={
                                activeItemId === item.id
                                  ? "blue.50"
                                  : "transparent"
                              }
                              borderLeft={
                                activeItemId === item.id ? "2px solid" : "none"
                              }
                              borderColor="blue.500"
                              cursor="pointer"
                              transition="all 0.2s"
                              _hover={{
                                bg:
                                  activeItemId === item.id
                                    ? "blue.50"
                                    : "gray.50",
                                textDecoration: "none",
                                color:
                                  activeItemId === item.id
                                    ? "blue.700"
                                    : "gray.800",
                              }}
                            >
                              {item.title}
                              <Badge
                                colorScheme={badgeColor}
                                fontSize="xs"
                                textTransform="uppercase"
                                ml={2}
                              >
                                {item.type === "directory"
                                  ? "Folder"
                                  : item.type}
                              </Badge>
                            </Link>
                          );
                        })}
                      </VStack>
                    </Collapse>
                  </Box>
                ))
              ) : (
                <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                  No results found for "{searchQuery}"
                </Text>
              )}
            </VStack>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default SearchableSidebar;
